import React, {useEffect, useMemo} from 'react';

import {useGate, useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';
import 'core/yupCustomValidations';
import {Row} from 'react-table';

import {ProductGroupIcon} from 'components/common/ProductGroupIcon';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {CheckboxArray} from 'components/ui-kit/CheckboxArray';
import {Select} from 'components/ui-kit/Select';
import {mapTableColumns, Table} from 'components/ui-kit/Table2.0';
import {ProductGroups} from 'core/constants/productGroups';
import {stepChanged, utilisationForm$, utilisationFormChanged} from 'models/documents/create';
import {
    filtersChanged,
    filtersReset,
    Gate,
    pageNumberChanged,
    productsNames$,
    initWithAffixingFilters,
    tableState$,
} from 'models/products/grid/table';
import {
    GridFieldConfig,
    ProductsGridAccessors,
    ProductsGridFormattedRow,
    ProductsGridRawRow,
} from 'types';
import {PackageTypes} from 'types/gtins';
import {handleNil} from 'utils/handleNil';
import {mapEnumToI18nOptions} from 'utils/select';

import {Input} from '../../../ui-kit/Input';
import {MarkingCodeFilter} from '../../ProductsGrid/Table/MarkingCodeFilter';
import {HeadContainer, StepNavigationContainer} from '../common/styled';

import {Wrapper} from './styled';

const getGridColumns = (t: TFunction): GridFieldConfig[] => [
    {
        id: ProductsGridAccessors.productGroupCode,
        header: t('pages.products.grid.productGroup'),
        filter: (
            <Select
                options={mapEnumToI18nOptions(ProductGroups, 'dictionaries.productGroups')}
                isClearable
                isMulti
            />
        ),
        isSortable: false,
        maxWidth: 150,
    },
    {
        id: ProductsGridAccessors.name,
        header: t('pages.products.grid.productName'),
        filter: (
            <CheckboxArray
                searchable={true}
                placeholder="Enter the product name"
                name="product-name-filter"
                options={() => productsNames$.getState()}
            />
        ),
    },
    {
        id: ProductsGridAccessors.code,
        header: t('pages.products.grid.code'),
        filter: <Input placeholder={t('Enter value')} />,
        isSortable: false,
    },
    {
        id: ProductsGridAccessors.gtin,
        header: t('pages.products.grid.gtin'),
        filter: <Input placeholder={t('Enter value')} />,
        isSortable: false,
    },
];

const convertData = (
    data: ProductsGridRawRow[] | undefined,
): Pick<
    ProductsGridFormattedRow,
    | ProductsGridAccessors.name
    | ProductsGridAccessors.productGroupCode
    | ProductsGridAccessors.code
    | ProductsGridAccessors.gtin
>[] => {
    if (!data) return [];

    return data.map((elem) => ({
        [ProductsGridAccessors.productGroupCode]: handleNil(elem, (row: ProductsGridRawRow) =>
            [
                PackageTypes.CUSTOMS_UNIT,
                PackageTypes.BOX,
                PackageTypes.PALLET,
                PackageTypes.CONTAINER,
            ].includes(row.packagingType) ? (
                <div>—</div>
            ) : (
                <ProductGroupIcon value={row.productGroupCode} label />
            ),
        ),
        [ProductsGridAccessors.name]: handleNil(elem.productName),
        [ProductsGridAccessors.code]: handleNil(elem.markingCode),
        [ProductsGridAccessors.gtin]: handleNil(elem.gtin),
    }));
};

export const UtilisationDocumentGroupCodes: React.FC = () => {
    const {t} = useTranslation();
    useGate(Gate, {withAffixingFilters: true});

    const utilisationForm = useStore(utilisationForm$);

    const {data, pageNumber, isLoading, filters} = useStore(tableState$);
    const columns = useMemo(
        () => mapTableColumns(getGridColumns(t), filters, filtersChanged, filtersReset),
        [t, filters],
    );
    const mappedData = React.useMemo(() => convertData(data?.content), [data]);

    const isHighlighted = (row: Row<ProductsGridFormattedRow>) =>
        utilisationForm.groupMarkingCode === row.original[ProductsGridAccessors.code];

    const onSelect = (row: Row<ProductsGridFormattedRow>) => {
        utilisationFormChanged({groupMarkingCode: row.original[ProductsGridAccessors.code]});
    };

    return (
        <Wrapper>
            <HeadContainer>
                <MarkingCodeFilter />
                <StepNavigationContainer>
                    <Button
                        style={{width: 'auto'}}
                        variant={ButtonVariant.Secondary}
                        onClick={() => stepChanged(0)}
                    >
                        {t('components.form.prevStep')}
                    </Button>
                    <Button
                        style={{width: 'auto'}}
                        variant={ButtonVariant.Normal}
                        onClick={() => stepChanged(2)}
                        disabled={
                            !utilisationForm.groupMarkingCode || !data?.content.length || isLoading
                        }
                    >
                        {t('components.form.nextStep')}
                    </Button>
                </StepNavigationContainer>
            </HeadContainer>
            <Table
                columns={columns}
                data={mappedData}
                hasNext={!!data?.nextPageRef}
                pageIndex={pageNumber}
                onPageChange={pageNumberChanged}
                isLoading={isLoading}
                onRowClick={onSelect}
                highlightedRow={isHighlighted}
                hideSetting
            />
        </Wrapper>
    );
};
